import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  FaUsers,
  FaShoppingCart,
  FaDollarSign,
  FaUserCheck,
  FaBox,
  FaEye,
  FaExclamationTriangle,
  FaSync,
  FaUser,
} from "react-icons/fa";
import {
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import "./dashboard.css";
import dashboardService from "../../services/dashboardService";
import { toast } from "react-toastify";
import { Col, Row, Carousel, Image } from "react-bootstrap";

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, isLoading } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [period, setPeriod] = useState("30 Days");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Dashboard data states
  const [kpiStats, setKpiStats] = useState([]);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [orderStatusData, setOrderStatusData] = useState([]);
  const [categorySales, setCategorySales] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [topVendors, setTopVendors] = useState([]);
  const [newVendors, setNewVendors] = useState([]);
  const [recentReviews, setRecentReviews] = useState([]);
  const [reviewFilter, setReviewFilter] = useState("all");

  const userType = user?.user_type || "admin";
  console.log("userType", userType)
  const [isAdmin, setIsAdmin] = useState(userType == "admin" || userType == "super_admin" || userType == "vendor");

  // Color scheme for charts
  const CHART_COLORS = {
    revenue: "#10b981",
    orders: "#3b82f6",
    products: "#00bfa5",
    vendors: "#f59e0b",
    users: "#8b5cf6",
    inventory: "#ef4444",
    pending: "#f59e0b",
    processing: "#3b82f6",
    delivered: "#10b981",
    cancelled: "#ef4444",
  };

  // Status colors for order status
  const getStatusColor = (status) => {
    const colors = {
      placed: "#6366f1",
      accepted: "#3b82f6",
      processing: "#8b5cf6",
      packed: "#ec4899",
      out_for_delivery: "#f59e0b",
      delivered: "#10b981",
      cancelled: "#ef4444",
      returned: "#64748b",
    };
    return colors[status] || "#64748b";
  };

  // Fetch all dashboard data
  const fetchDashboardData = useCallback(async (isInitialLoad = false) => {
    // Check if user is authenticated before fetching
    const token = localStorage.getItem("authToken");
    if (!token || !user) {
      // User is not authenticated, don't fetch data
      if (isInitialLoad) {
        setLoading(false);
      }
      return;
    }

    try {
      // Only show full-page loading on initial load
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }
      if (isAdmin) {
        // Use custom dates if at least one is provided
        const useCustomDates = startDate || endDate;
        // Pass both dates, using null/undefined for missing ones
        const dateParams = useCustomDates ? [startDate || null, endDate || null] : [];

        // Fetch admin dashboard data
        const [
          statsRes,
          revenueRes,
          orderStatusRes,
          categoryRes,
          productsRes,
          ordersRes,
          lowStockRes,
          topVendorsRes,
          newVendorsRes,
          reviewsRes,
        ] = await Promise.all([
          dashboardService.getComprehensiveStats(period, ...dateParams),
          dashboardService.getRevenueTrend(period, ...dateParams),
          dashboardService.getOrderStatusDistribution(period, ...dateParams),
          dashboardService.getSalesByCategory(period, 10, ...dateParams),
          dashboardService.getTopProducts(period, 10, ...dateParams),
          dashboardService.getRecentOrders(5),
          dashboardService.getLowStockProducts(10),
          dashboardService.getTopVendors(10),
          dashboardService.getNewVendors(10),
          dashboardService.getRecentReviews(5),
        ]);

        // Set KPI Stats
        const overview = statsRes?.overview || {};
        setKpiStats([
          {
            title: "Active Products",
            value: formatNumber(overview.activeProducts || 0),
            change: "+0%",
            changeType: "positive",
            icon: <FaBox />,
            className: "star-icon-bg-color1",
            colorClass: "stat-value-color1",
          },
          {
            title: "Active Vendors",
            value: formatNumber(overview.activeVendors || 0),
            change: `${overview.pendingVendors || 0} pending`,
            changeType: overview.pendingVendors > 0 ? "warning" : "positive",
            icon: <FaUserCheck />,
            className: "star-icon-bg-color2",
            colorClass: "stat-value-color2",
          },
          {
            title: "Total Customers",
            value: formatNumber(overview.totalUsers || 0),
            change: "+0%",
            changeType: "positive",
            icon: <FaUsers />,
            className: "star-icon-bg-color3",
            colorClass: "stat-value-color3",
          },
          {
            title: "Delivered Orders",
            value: formatNumber(overview.deliveredOrders || 0),
            change: "+0%",
            changeType: "positive",
            icon: <FaShoppingCart />,
            className: "star-icon-bg-color4",
            colorClass: "stat-value-color4",
          },
        ]);

        // Set Revenue Trend
        const trendData = revenueRes?.trend || [];
        setRevenueTrend(
          trendData.map((item) => ({
            date: formatDate(item.date),
            revenue: parseFloat(item.revenue || 0),
            orders: parseInt(item.order_count || 0),
          }))
        );

        // Set Order Status Distribution
        const statusData = orderStatusRes?.distribution || [];
        setOrderStatusData(statusData);

        // Set Category Sales
        const categories = categoryRes?.categories || [];
        setCategorySales(categories);

        // Set Top Products
        const products = productsRes?.products || [];
        setTopProducts(products);

        // Set Recent Orders
        const orders = ordersRes?.orders || [];
        setRecentOrders(orders);

        // Set Low Stock Products
        const lowStock = lowStockRes?.products || [];
        setLowStockProducts(lowStock);

        // Set Top Vendors
        const vendors = topVendorsRes?.vendors || [];
        setTopVendors(vendors);

        // Set New Vendors
        const newestVendors = newVendorsRes?.vendors || [];
        setNewVendors(newestVendors);

        // Set Recent Reviews
        const reviews = reviewsRes?.reviews || [];
        setRecentReviews(reviews);
      } else if (userType === "vendor") {
        // Use custom dates if at least one is provided
        const useCustomDates = startDate || endDate;
        // Pass both dates, using null/undefined for missing ones
        const dateParams = useCustomDates ? [startDate || null, endDate || null] : [];

        // Fetch vendor dashboard data
        const [vendorStatsRes, vendorProductsRes] = await Promise.all([
          dashboardService.getVendorDashboardStats(),
          dashboardService.getTopProducts(period, 10, ...dateParams),
        ]);

        const stats = vendorStatsRes || {};
        setKpiStats([
          {
            title: "My Products",
            value: formatNumber(stats.totalProducts || 0),
            change: `+0`,
            changeType: "positive",
            icon: <FaBox />,
            color: CHART_COLORS.products,
          },
          {
            title: "Total Orders",
            value: formatNumber(stats.totalOrders || 0),
            change: "+0%",
            changeType: "positive",
            icon: <FaShoppingCart />,
            color: CHART_COLORS.orders,
          },
          {
            title: "My Revenue",
            value: `₹${formatNumber(stats.totalRevenue || 0)}`,
            change: "+0%",
            changeType: "positive",
            icon: <FaDollarSign />,
            color: CHART_COLORS.revenue,
          },
          {
            title: "Low Stock",
            value: formatNumber(stats.lowStockProducts || 0),
            change: "items",
            changeType: stats.lowStockProducts > 0 ? "warning" : "positive",
            icon: <FaExclamationTriangle />,
            color: CHART_COLORS.inventory,
          },
        ]);

        const products = vendorProductsRes?.products || [];
        setTopProducts(products);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);

      // Only show error toast if:
      // 1. User is authenticated (has token)
      // 2. Error is not a 401 (unauthorized) - which means user is not authenticated
      // 3. We're not on the login page
      const token = localStorage.getItem("authToken");
      const isUnauthorized = error.response?.status === 401;
      const isOnLoginPage = window.location.pathname === "/login";

      if (token && !isUnauthorized && !isOnLoginPage) {
        toast.error("Failed to load dashboard data");
      }
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setIsRefreshing(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, period, startDate, endDate, userType]);

  // Track if initial load is done using ref to avoid re-renders
  const initialLoadDoneRef = useRef(false);

  // Initial load effect - only runs once when user is available
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    // Only fetch if user is authenticated and we haven't loaded yet
    if (user && token && !initialLoadDoneRef.current) {
      fetchDashboardData(true).then(() => {
        initialLoadDoneRef.current = true;
      });
    } else if (!user || !token) {
      // Reset when user logs out or token is missing
      initialLoadDoneRef.current = false;
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Only depend on user for initial load

  // Effect for date/period changes - debounced, only after initial load
  useEffect(() => {
    if (user && initialLoadDoneRef.current) {
      // Only fetch if initial load is done
      const timeoutId = setTimeout(() => {
        fetchDashboardData(false);
      }, 300); // 300ms debounce

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, period]); // Only trigger on filter changes

  // Fetch reviews based on rating filter
  const fetchRecentReviews = useCallback(async (ratingVal) => {
    try {
      const res = await dashboardService.getRecentReviews(15, ratingVal);
      setRecentReviews(res?.reviews || []);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    }
  }, []);

  // Fetch reviews when filter changes
  useEffect(() => {
    if (user && initialLoadDoneRef.current && isAdmin) {
      fetchRecentReviews(reviewFilter);
    }
  }, [reviewFilter, user, isAdmin, fetchRecentReviews]);

  // Format number with commas
  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-IN").format(num);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", { month: "short", day: "numeric" });
  };

  // Generate consistent color and style for avatar placeholders
  const getAvatarStyle = (name) => {
    if (!name || name === "Anonymous Customer" || name === "Anonymous") {
      return {
        background: "#e2e8f0",
        color: "#64748b"
      };
    }

    // Hash function to choose a consistent color based on name
    const colors = [
      "#3b82f6", // Blue
      "#10b981", // Green
      "#8b5cf6", // Purple
      "#ec4899", // Pink
      "#f59e0b", // Yellow
      "#ef4444", // Red
      "#06b6d4", // Cyan
      "#14b8a6"  // Teal
    ];

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;

    return {
      background: colors[index],
      color: "#ffffff"
    };
  };

  // Format time ago
  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600)
      return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  };

  // Format stock quantity with unit
  const formatStockWithUnit = (quantity, unit) => {
    const qty = Math.max(0, parseInt(quantity) || 0);
    const unitStr = unit ? ` ${unit}` : "";
    return `${qty}${unitStr}`;
  };

  // Convert status to title case
  const toTitleCase = (str) => {
    if (!str) return "";
    return str
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Handle card click navigation
  const handleCardClick = (title) => {
    switch (title) {
      case "Active Products":
        navigate("/product");
        break;
      case "Active Vendors":
        navigate("/admin/vendors");
        break;
      case "Delivered Orders":
        navigate("/admin/orders?order_status=delivered");
        break;
      default:
        break;
    }
  };

  // Period buttons
  const periodButtons = [
    { label: "7 Days", value: "7d" },
    { label: "30 Days", value: "30d" },
    { label: "90 Days", value: "90d" },
    { label: "1 Year", value: "1y" },
  ];

  if (isLoading || loading) {
    return (
      <div className="dashboard">
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="dashboard">
        <div className="dashboard-welcome">
          <div className="welcome-content">
            <h1>Welcome to Koyo Farm Dashboard!</h1>
            <p>Please log in to access your personalized dashboard.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Header Section */}
      <div className="dashboard-header" style={{ background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)", border: "1px solid rgba(255, 255, 255, 0.05)" }}>
        <div>
          <h1 style={{ color: "#ffffff", marginBottom: "8px" }}>Welcome by Book Edu, {user.first_name || 'Admin'}!</h1>
          <p style={{ color: "#94a3b8" }}>Real-time analytics and management dashboard</p>
        </div>
        <div className="header-actions">
          <div className="period-selector">
            {periodButtons.map((btn) => (
              <button
                key={btn.value}
                className={`period-btn ${period === btn.value ? "active" : ""}`}
                onClick={() => setPeriod(btn.value)}
              >
                {btn.label}
              </button>
            ))}
          </div>
          <div className="date-filters d-flex gap-2 align-items-center">
            <input
              type="date"
              className="form-control form-control-sm"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ maxWidth: "140px", background: "rgba(255,255,255,0.05)", color: "#fff", borderColor: "rgba(255,255,255,0.1)" }}
            />
            <span style={{ color: "#94a3b8" }}>to</span>
            <input
              type="date"
              className="form-control form-control-sm"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ maxWidth: "140px", background: "rgba(255,255,255,0.05)", color: "#fff", borderColor: "rgba(255,255,255,0.1)" }}
            />
            {(startDate || endDate) && (
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                }}
              >
                Clear
              </button>
            )}
          </div>
          <button
            className="refresh-btn"
            onClick={() => fetchDashboardData(false)}
            disabled={isRefreshing}
          >
            <FaSync className={isRefreshing ? "spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* KPI Stats Grid */}
      <div className="stats-grid">
        {kpiStats.map((stat, index) => (
          <div
            key={index}
            className="stat-card"
            onClick={() => handleCardClick(stat.title)}
            style={{ cursor: "pointer" }}
          >
            <div className="stat-header">
              <div className={`stat-icon ${stat.className || ""}`} style={{ backgroundColor: stat.color }}>
                {stat.icon}
              </div>
              <div className="stat-change">
                <span className="stat-title">{stat.title}</span>
                <h3 className={`stat-value ${stat.colorClass || ""}`} style={{ color: stat.color }}>{stat.value}</h3>
              </div>
            </div>
            {stat.change && (
              <div className="stat-change-indicator">
                <span className={stat.changeType === "positive" ? "change-positive" : "change-warning"}>
                  {stat.change}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="charts-row">
        {/* Revenue & Sales Trend */}
        <div className="chart-container">
          <div className="chart-header">
            <h3>Revenue & Orders Trend</h3>
            <p>Sales development over time</p>
          </div>
          <div style={{ width: "100%", height: 300 }}>
            {revenueTrend.length > 0 ? (
              <ResponsiveContainer>
                <AreaChart data={revenueTrend} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLORS.revenue} stopOpacity={0.8} />
                      <stop offset="95%" stopColor={CHART_COLORS.revenue} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickLine={false} />
                  <YAxis yAxisId="left" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis yAxisId="right" orientation="right" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} />
                  <Legend verticalAlign="top" height={36} />
                  <Area yAxisId="left" type="monotone" dataKey="revenue" name="Revenue (₹)" stroke={CHART_COLORS.revenue} fillOpacity={1} fill="url(#colorRevenue)" />
                  <Line yAxisId="right" type="monotone" dataKey="orders" name="Orders Count" stroke={CHART_COLORS.orders} strokeWidth={2} activeDot={{ r: 8 }} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                No trend data available for the selected period.
              </div>
            )}
          </div>
        </div>

        {/* Order Status Distribution */}
        <div className="chart-container">
          <div className="chart-header">
            <h3>Order Status Distribution</h3>
            <p>Breakdown of orders by fulfillment status</p>
          </div>
          <div style={{ width: "100%", height: 300 }} className="d-flex align-items-center justify-content-center">
            {orderStatusData.length > 0 ? (
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={orderStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={3}
                    dataKey="count"
                    nameKey="status"
                    label={({ name, percent }) => `${toTitleCase(name)} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {orderStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getStatusColor(entry.status)} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [value, toTitleCase(name)]} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-muted">No orders placed in this period.</span>
            )}
          </div>
        </div>
      </div>

      {/* Secondary Charts Row */}
      <div className="charts-row">
        {/* Sales by Category */}
        <div className="chart-container">
          <div className="chart-header">
            <h3>Sales by Category</h3>
            <p>Product categories by revenue</p>
          </div>
          <div style={{ width: "100%", height: 300 }}>
            {categorySales.length > 0 ? (
              <ResponsiveContainer>
                <BarChart data={categorySales}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} tickLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} formatter={(value) => [`₹${formatNumber(value)}`, "Revenue"]} />
                  <Bar dataKey="total_revenue" name="Revenue" fill={CHART_COLORS.users} radius={[4, 4, 0, 0]}>
                    {categorySales.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={index % 2 === 0 ? CHART_COLORS.users : CHART_COLORS.orders} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                No category sales recorded.
              </div>
            )}
          </div>
        </div>

        {/* Top Selling Products */}
        <div className="chart-container">
          <div className="chart-header">
            <h3>Top Selling Products</h3>
            <p>Most popular items by quantity sold</p>
          </div>
          <div style={{ width: "100%", height: 300 }}>
            {topProducts.length > 0 ? (
              <ResponsiveContainer>
                <BarChart data={topProducts} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                  <XAxis type="number" stroke="#94a3b8" fontSize={11} tickLine={false} />
                  <YAxis dataKey="name" type="category" stroke="#94a3b8" fontSize={10} width={100} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} />
                  <Bar dataKey="total_quantity" name="Quantity Sold" fill={CHART_COLORS.products} radius={[0, 4, 4, 0]} barSize={15} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                No products sold in this period.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Grid for lists & tables */}
      <div className="dashboard-grid">
        {/* Recent Orders */}
        <div className="dashboard-section">
          <div className="section-header d-flex justify-content-between align-items-center">
            <div>
              <h2>Recent Orders</h2>
              <p>Latest transactions across the platform</p>
            </div>
            <Link to="/admin/orders" className="view-all-link">
              View All Orders &rarr;
            </Link>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.length > 0 ? (
                  recentOrders.map((order) => (
                    <tr key={order.id}>
                      <td>
                        <Link to={`/admin/orders/details/${order.id}`} className="order-link fw-semibold">
                          {order.order_no ? `#${order.order_no}` : `#${order.id.substring(0, 8)}...`}
                        </Link>
                      </td>
                      <td>{order.customer_name}</td>
                      <td>{formatDate(order.created_at)}</td>
                      <td>₹{formatNumber(order.amount)}</td>
                      <td>
                        <span className="status-badge" style={{ backgroundColor: getStatusColor(order.status) + "20", color: getStatusColor(order.status) }}>
                          {toTitleCase(order.status)}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="no-data">No recent orders found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Low Stock Alerts */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>Low Stock Alerts</h2>
            <p>Items requiring replenishment</p>
          </div>
          <div className="alert-list">
            {lowStockProducts.length > 0 ? (
              lowStockProducts.map((prod) => (
                <div key={prod.id} className="alert-item" style={{ borderLeftColor: CHART_COLORS.inventory }}>
                  <div className="alert-icon" style={{ backgroundColor: CHART_COLORS.inventory }}>
                    <FaExclamationTriangle />
                  </div>
                  <div className="alert-content">
                    <p className="alert-product-name">{prod.product_name}</p>
                    <p className="alert-stock-info">
                      Current Stock: <strong style={{ color: CHART_COLORS.inventory }}>{formatStockWithUnit(prod.stock_quantity, prod.unit)}</strong> (Limit: {prod.low_stock_alert || 10})
                    </p>
                  </div>
                  <Link to={`/product/edit/${prod.id}`} className="alert-action">
                    Restock
                  </Link>
                </div>
              ))
            ) : (
              <div className="no-alerts">
                <p>All products are sufficiently stocked!</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Vendors & New Vendors Row */}
      <div className="mt-4 mb-4">
        <Row>
          {/* Top Performing Vendors */}
          <Col md={6} className="mb-4">
            <div className="dashboard-section h-100">
              <div className="section-header">
                <h2>Top Performing Vendors</h2>
                <p>Vendors generating the highest sales revenue</p>
              </div>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Vendor Name</th>
                      <th>Status</th>
                      <th>Orders</th>
                      <th>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topVendors.length > 0 ? (
                      topVendors.slice(0, 5).map((vendor) => (
                        <tr key={vendor.id}>
                          <td>
                            <span className="fw-semibold">{vendor.name}</span>
                          </td>
                          <td>
                            <span className="status-badge" style={{
                              backgroundColor: vendor.status === 'Approved' ? '#10b98120' : '#ef444420',
                              color: vendor.status === 'Approved' ? '#10b981' : '#ef4444'
                            }}>
                              {vendor.status}
                            </span>
                          </td>
                          <td>{vendor.order_count}</td>
                          <td>₹{formatNumber(vendor.total_revenue)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="no-data">No vendor performance data found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </Col>

          {/* Newly Joined Vendors */}
          <Col md={6} className="mb-4">
            <div className="dashboard-section h-100">
              <div className="section-header">
                <h2>Newly Joined Vendors</h2>
                <p>Vendors recently registered on the platform</p>
              </div>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Vendor Name</th>
                      <th>Status</th>
                      <th>Joined Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {newVendors.length > 0 ? (
                      newVendors.slice(0, 5).map((vendor) => (
                        <tr key={vendor.id}>
                          <td>
                            <span className="fw-semibold">{vendor.name}</span>
                          </td>
                          <td>
                            <span className="status-badge" style={{
                              backgroundColor: vendor.status === 'Approved' ? '#10b98120' : '#f59e0b20',
                              color: vendor.status === 'Approved' ? '#10b981' : '#f59e0b'
                            }}>
                              {vendor.status}
                            </span>
                          </td>
                          <td>{formatDate(vendor.joined_date)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="no-data">No new vendor registrations found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Customer Testimonials & Reviews */}
      <div className="dashboard-section mt-4 mb-4" style={{ overflow: "hidden", maxWidth: "100%" }}>
        <div className="section-header d-flex justify-content-between align-items-center">
          <div>
            <h2>Customer Testimonials & Reviews</h2>
            <p>Recent ratings and feedback testimonials from buyers</p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span style={{ fontSize: "0.85rem", color: "#64748b", fontWeight: "600" }}>Filter Rating:</span>
            <select
              className="form-select form-select-sm"
              value={reviewFilter}
              onChange={(e) => setReviewFilter(e.target.value)}
              style={{
                maxWidth: "130px",
                borderRadius: "8px",
                borderColor: "#e2e8f0",
                fontSize: "0.85rem",
                fontWeight: "600",
                color: "#1e293b",
                background: "#ffffff",
                padding: "6px 12px"
              }}
            >
              <option value="all">All Ratings</option>
              <option value="5">5 Stars</option>
              <option value="4">4 Stars</option>
              <option value="3">3 Stars</option>
              <option value="2">2 Stars</option>
              <option value="1">1 Star</option>
            </select>
          </div>
        </div>

        {recentReviews.length > 0 ? (
          <div className="tm-marquee-wrapper">
            <div className="tm-container">
              <div className="tm-track">
                {/* First Set of reviews */}
                {recentReviews.map((review) => (
                  <div key={`${review.id}-1`} className="tm-card-wrapper">
                    <div className="tm-card">
                      {/* Top row: Rating & Verified Purchase tag */}
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center gap-1">
                          <div className="d-flex">
                            {Array.from({ length: 5 }).map((_, idx) => (
                              <span
                                key={idx}
                                style={{
                                  color: idx < review.rating ? "#f59e0b" : "#cbd5e1",
                                  fontSize: "1.1rem",
                                  marginRight: "2px"
                                }}
                              >
                                ★
                              </span>
                            ))}
                          </div>
                          <span style={{ fontSize: "0.85rem", fontWeight: "700", color: "#475569" }}>
                            ({review.rating.toFixed(1)})
                          </span>
                        </div>

                        {/* Verified Badge */}
                        {review.is_verified ? (
                          <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1" style={{ fontSize: "0.7rem", borderRadius: "6px" }}>
                            Verified Purchase
                          </span>
                        ) : (
                          <span className="badge bg-light text-muted border px-2 py-1" style={{ fontSize: "0.7rem", borderRadius: "6px" }}>
                            Standard Review
                          </span>
                        )}
                      </div>

                      {/* Review Title */}
                      <h5 style={{
                        fontSize: "1.05rem",
                        fontWeight: "700",
                        color: "#1e293b",
                        marginBottom: "6px"
                      }}>
                        {review.title || "Excellent Product!"}
                      </h5>

                      {/* Review Text */}
                      <p style={{
                        fontSize: "0.88rem",
                        color: "#475569",
                        lineHeight: "1.5",
                        fontStyle: "italic",
                        marginBottom: "12px",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        height: "60px"
                      }}>
                        "{review.comment}"
                      </p>

                      {/* Review Images */}
                      {review.review_images && review.review_images.length > 0 ? (
                        <div className="d-flex gap-1 mb-2 flex-wrap" style={{ height: "35px" }}>
                          {review.review_images.slice(0, 3).map((img, idx) => (
                            <img
                              key={idx}
                              src={img}
                              alt="Review Attach"
                              style={{
                                width: "35px",
                                height: "35px",
                                borderRadius: "4px",
                                objectFit: "cover",
                                border: "1px solid #e2e8f0"
                              }}
                            />
                          ))}
                        </div>
                      ) : (
                        <div style={{ height: "35px", marginBottom: "8px" }} />
                      )}

                      {/* Helpful Counts & Order ID */}
                      <div className="d-flex justify-content-between align-items-center mb-3 text-muted" style={{ fontSize: "0.75rem", borderTop: "1px dashed #e2e8f0", paddingTop: "8px" }}>
                        <span>
                          Helpful: <strong className="text-success">{review.helpful_count}</strong> | Not: <strong className="text-danger">{review.unhelpful_count}</strong>
                        </span>
                        {review.order_id && (
                          <span className="text-truncate" style={{ maxWidth: "140px" }}>
                            Order: #{review.order_id.substring(0, 8)}...
                          </span>
                        )}
                      </div>

                      {/* Product & Vendor Details */}
                      {/* {console.log("review.product_image", review.product_image?.join(","))} */}
                      <div className="d-flex align-items-center mb-3 p-2 bg-light rounded" style={{ fontSize: "0.8rem" }}>
                        {(review.product_image && review.product_image.trim() !== "" && review.product_image != "null" && review.product_image != "undefined") ? (
                          <img
                            src={JSON.parse(review.product_image)?.[0]}
                            alt={review.product_name}
                            style={{
                              width: "35px",
                              height: "35px",
                              borderRadius: "6px",
                              objectFit: "cover",
                              marginRight: "8px"
                            }}
                          />
                        ) : (
                          <div style={{
                            width: "35px",
                            height: "35px",
                            borderRadius: "6px",
                            background: "#e2e8f0",
                            color: "#475569",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "700",
                            fontSize: "0.9rem",
                            marginRight: "8px"
                          }}>
                            {(review.product_name || "P").charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="text-truncate" style={{ flex: 1 }}>
                          <div className="text-truncate fw-semibold text-dark">{review.product_name}</div>
                          <div className="text-truncate text-muted" style={{ fontSize: "0.75rem" }}>
                            Vendor: <span className="text-dark fw-medium">{review.vendor_name}</span>
                          </div>
                        </div>
                      </div>

                      {/* Reviewer & Date Details */}
                      <div className="d-flex align-items-center justify-content-between mt-auto pt-2 border-top" style={{ borderColor: "#f1f5f9" }}>
                        <div className="d-flex align-items-center gap-2">
                          {/* Customer Avatar */}
                          <div
                            style={{
                              width: "30px",
                              height: "30px",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "bold",
                              fontSize: "0.8rem",
                              ...getAvatarStyle(review.customer_name)
                            }}
                          >
                            {(!review.customer_name || review.customer_name === "Anonymous Customer" || review.customer_name === "Anonymous") ? (
                              <FaUser size={12} />
                            ) : (
                              review.customer_name.charAt(0).toUpperCase()
                            )}
                          </div>
                          <span style={{ fontSize: "0.8rem", fontWeight: "700", color: "#334155" }}>
                            {review.customer_name}
                          </span>
                        </div>
                        <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                          {formatDate(review.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
                {/* Second Set of reviews (duplicate for infinite scroll) */}
                {recentReviews.map((review) => (
                  <div key={`${review.id}-2`} className="tm-card-wrapper">
                    <div className="tm-card">
                      {/* Top row: Rating & Verified Purchase tag */}
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center gap-1">
                          <div className="d-flex">
                            {Array.from({ length: 5 }).map((_, idx) => (
                              <span
                                key={idx}
                                style={{
                                  color: idx < review.rating ? "#f59e0b" : "#cbd5e1",
                                  fontSize: "1.1rem",
                                  marginRight: "2px"
                                }}
                              >
                                ★
                              </span>
                            ))}
                          </div>
                          <span style={{ fontSize: "0.85rem", fontWeight: "700", color: "#475569" }}>
                            ({review.rating.toFixed(1)})
                          </span>
                        </div>

                        {/* Verified Badge */}
                        {review.is_verified ? (
                          <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1" style={{ fontSize: "0.7rem", borderRadius: "6px" }}>
                            Verified Purchase
                          </span>
                        ) : (
                          <span className="badge bg-light text-muted border px-2 py-1" style={{ fontSize: "0.7rem", borderRadius: "6px" }}>
                            Standard Review
                          </span>
                        )}
                      </div>

                      {/* Review Title */}
                      <h5 style={{
                        fontSize: "1.05rem",
                        fontWeight: "700",
                        color: "#1e293b",
                        marginBottom: "6px"
                      }}>
                        {review.title || "Excellent Product!"}
                      </h5>

                      {/* Review Text */}
                      <p style={{
                        fontSize: "0.88rem",
                        color: "#475569",
                        lineHeight: "1.5",
                        fontStyle: "italic",
                        marginBottom: "12px",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        height: "60px"
                      }}>
                        "{review.comment}"
                      </p>

                      {/* Review Images */}
                      {review.review_images && review.review_images.length > 0 ? (
                        <div className="d-flex gap-1 mb-2 flex-wrap" style={{ height: "35px" }}>
                          {review.review_images.slice(0, 3).map((img, idx) => (
                            <img
                              key={idx}
                              src={img}
                              alt="Review Attach"
                              style={{
                                width: "35px",
                                height: "35px",
                                borderRadius: "4px",
                                objectFit: "cover",
                                border: "1px solid #e2e8f0"
                              }}
                            />
                          ))}
                        </div>
                      ) : (
                        <div style={{ height: "35px", marginBottom: "8px" }} />
                      )}

                      {/* Helpful Counts & Order ID */}
                      <div className="d-flex justify-content-between align-items-center mb-3 text-muted" style={{ fontSize: "0.75rem", borderTop: "1px dashed #e2e8f0", paddingTop: "8px" }}>
                        <span>
                          Helpful: <strong className="text-success">{review.helpful_count}</strong> | Not: <strong className="text-danger">{review.unhelpful_count}</strong>
                        </span>
                        {review.order_id && (
                          <span className="text-truncate" style={{ maxWidth: "140px" }}>
                            Order: #{review.order_id.substring(0, 8)}...
                          </span>
                        )}
                      </div>

                      {/* Product & Vendor Details */}
                      <div className="d-flex align-items-center mb-3 p-2 bg-light rounded" style={{ fontSize: "0.8rem" }}>
                        {(review.product_image && review.product_image.trim() !== "" && review.product_image !== "null" && review.product_image !== "undefined") ? (
                          <img
                            src={review.product_image}
                            alt={review.product_name}
                            style={{
                              width: "35px",
                              height: "35px",
                              borderRadius: "6px",
                              objectFit: "cover",
                              marginRight: "8px"
                            }}
                          />
                        ) : (
                          <div style={{
                            width: "35px",
                            height: "35px",
                            borderRadius: "6px",
                            background: "#e2e8f0",
                            color: "#475569",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "700",
                            fontSize: "0.9rem",
                            marginRight: "8px"
                          }}>
                            {(review.product_name || "P").charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="text-truncate" style={{ flex: 1 }}>
                          <div className="text-truncate fw-semibold text-dark">{review.product_name}</div>
                          <div className="text-truncate text-muted" style={{ fontSize: "0.75rem" }}>
                            Vendor: <span className="text-dark fw-medium">{review.vendor_name}</span>
                          </div>
                        </div>
                      </div>

                      {/* Reviewer & Date Details */}
                      <div className="d-flex align-items-center justify-content-between mt-auto pt-2 border-top" style={{ borderColor: "#f1f5f9" }}>
                        <div className="d-flex align-items-center gap-2">
                          {/* Customer Avatar */}
                          <div
                            style={{
                              width: "30px",
                              height: "30px",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "bold",
                              fontSize: "0.8rem",
                              ...getAvatarStyle(review.customer_name)
                            }}
                          >
                            {(!review.customer_name || review.customer_name === "Anonymous Customer" || review.customer_name === "Anonymous") ? (
                              <FaUser size={12} />
                            ) : (
                              review.customer_name.charAt(0).toUpperCase()
                            )}
                          </div>
                          <span style={{ fontSize: "0.8rem", fontWeight: "700", color: "#334155" }}>
                            {review.customer_name}
                          </span>
                        </div>
                        <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                          {formatDate(review.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center text-muted py-4">
            No recent testimonials or reviews found matching this rating filter.
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
